import { useState } from 'react';

import { AppIcons, AppIcon, ChartCardModal, ColorToken } from '@backrs_ui/common-ui';
import type { ModalData } from '@backrs_ui/common-ui';

import { Box, Chip, Typography, SxProps, Theme } from '@mui/material';

import { LineInfo, LineInfoIcon } from './LineInfoIcon';

type LineIconItemProps = Readonly<{
  title: string;
  iconName?: AppIcons;
  text?: string;
  info?: LineInfo;
  chip?: {
    label: string;
    color: ColorToken;
    modalData?: ModalData;
  };
}>;

export const LineIconItem = ({ title, iconName, text, info, chip }: LineIconItemProps) => {
  const [isChipModalOpen, setIsChipModalOpen] = useState(false);
  const titleId = title.toLowerCase().replace(/\s+/g, '-');

  return (
    <>
      <Box sx={containerSx}>
        <Box sx={titleContainerSx}>
          <Typography
            variant="text-md-semibold"
            color={({ backrs }) => backrs.colors['Brand/500']}
            sx={titleSx}
          >
            {title}
          </Typography>

          {info && <LineInfoIcon info={info} idPrefix={`line-icon-${titleId}`} />}
        </Box>

        {text && (
          <Box sx={valueContainerSx}>
            {iconName && <AppIcon iconName={iconName} diameter="20px" color="Colors/Base/Black" />}
            <Typography variant="text-md-medium">{text}</Typography>

            {chip && (
              <Chip
                id={`line-icon-chip-${chip.label.toLowerCase().replace(/\s+/g, '-')}`}
                variant="badge"
                size="sm"
                type="leading"
                label={chip.label}
                icon={
                  <AppIcon
                    iconName={chip.color === 'Error/400' ? 'ThumbsDown' : 'ThumbsUp'}
                    diameter="16px"
                    color={chip.color}
                  />
                }
                sx={chipSx(!!chip.modalData)}
                onClick={chip.modalData ? () => setIsChipModalOpen(true) : undefined}
              />
            )}
          </Box>
        )}
      </Box>

      {chip?.modalData && (
        <ChartCardModal
          isModalOpen={isChipModalOpen}
          setIsModalOpen={setIsChipModalOpen}
          modalData={chip.modalData}
        />
      )}
    </>
  );
};

const containerSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: backrs.spacing['spacing-md'],
});

const titleContainerSx: SxProps<Theme> = {
  display: 'flex',
  flexDirection: 'row',
  alignItems: 'center',
};

const titleSx: SxProps<Theme> = {
  width: 'max-content',
};

const valueContainerSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  flexDirection: 'row',
  gap: backrs.spacing['spacing-md'],
  alignItems: 'center',
});

const chipSx =
  (isClickable: boolean): SxProps<Theme> =>
  ({ backrs }) => ({
    border: `1px solid ${backrs.colors['Colors/Border/border-primary']}`,
    cursor: isClickable ? 'pointer' : 'default',
  });
